import React from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { CORPORATE_STRUCTURE_OPTIONS, FEIN_MASK, INDUSTRIES_OPTIONS } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { normalizeEmail } from '@clearverify/utils';

import { DefaultDisclaimerInputSection } from 'src/components/disclaimer';
import {
  AddressFields,
  AutocompleteField,
  MaskedTextField,
  NumberField,
  PhoneField,
  SelectField,
  TextField,
} from 'src/components/finalForm';
import { TAX_YEAR_END_DATE_MASK } from 'src/constants/masks';
import { useDemoPrivacyInputSlotProps } from 'src/hooks/demoPrivacyMode';
import { usePeoPayrollProviderById } from 'src/hooks/peoPayrollProvider';

import {
  PeoPayrollProviderCompanyInformationFormFields,
  PeoPayrollProviderCompanyInformationFormValues,
} from './types';
import {
  getPeoPayrollProviderCompanyInformationInitialValues,
  peoPayrollProviderCompanyInformationFormValidationSchema,
} from './utils';

type PeoPayrollProvidersCompanyInformationFormProps = {
  peoPayrollProviderId: Nullable<string>;
};

export const PeoPayrollProvidersDetailCompanyInformationForm: React.FC<
  PeoPayrollProvidersCompanyInformationFormProps
> = ({ peoPayrollProviderId }) => {
  const { peoPayrollProvider, updatePeoPayrollProvider, isPartnership } =
    usePeoPayrollProviderById(peoPayrollProviderId);
  const demoPrivacyInputSlotProps = useDemoPrivacyInputSlotProps();

  const initialValues = React.useMemo(
    () => getPeoPayrollProviderCompanyInformationInitialValues(peoPayrollProvider),
    [peoPayrollProvider],
  );

  const onSubmit = React.useCallback(
    async (formData: PeoPayrollProviderCompanyInformationFormValues) => {
      try {
        const headquarters = formData[PeoPayrollProviderCompanyInformationFormFields.headquarters];
        await updatePeoPayrollProvider({
          data: {
            displayName: formData.displayName,
            fein: formData.fein,
            industry: formData.industry?.value ?? null,
            corporateStructure: formData.corporateStructure ?? null,
            numberOfEmployees: formData.numberOfEmployees ?? null,
            contactEmail: normalizeEmail(formData.contactEmail),
            contactPhoneNumber: formData.contactPhoneNumber ?? null,
            taxYearEndDate: formData.taxYearEndDate ?? null,
            defaultDisclaimer: formData.defaultDisclaimer ?? null,
            headquarters: {
              update: {
                city: headquarters?.city ?? null,
                county: headquarters?.county ?? null,
                state: headquarters?.state ?? null,
                street: headquarters?.street ?? null,
                zipCode: headquarters?.zipCode ?? null,
              },
            },
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [updatePeoPayrollProvider],
  );

  return (
    <Form<PeoPayrollProviderCompanyInformationFormValues>
      onSubmit={onSubmit}
      initialValues={initialValues}
      subscription={{ valid: true, values: true, submitting: true }}
      validationSchema={peoPayrollProviderCompanyInformationFormValidationSchema}
    >
      {({ submitting, valid, form }) => {
        const { dirty } = form.getState();

        return (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Company Display Name"
                name={PeoPayrollProviderCompanyInformationFormFields.displayName}
                fullWidth
                required
                disabled={submitting}
                component={TextField}
                slotProps={demoPrivacyInputSlotProps}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                component={MaskedTextField}
                name={PeoPayrollProviderCompanyInformationFormFields.fein}
                label="FEIN"
                mask={FEIN_MASK}
                fullWidth
                disabled={submitting}
                required
                slotProps={demoPrivacyInputSlotProps}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Corporate Structure"
                name={PeoPayrollProviderCompanyInformationFormFields.corporateStructure}
                fullWidth
                disabled={submitting}
                options={CORPORATE_STRUCTURE_OPTIONS}
                component={SelectField}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Industry"
                name={PeoPayrollProviderCompanyInformationFormFields.industry}
                fullWidth
                disabled={submitting}
                options={INDUSTRIES_OPTIONS}
                component={AutocompleteField}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                fullWidth
                label="Tax Year End Date"
                name={PeoPayrollProviderCompanyInformationFormFields.taxYearEndDate}
                component={MaskedTextField}
                inputProps={{
                  placeholder: 'MM/DD',
                }}
                mask={TAX_YEAR_END_DATE_MASK}
                disabled={submitting}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Number of Employees"
                name={PeoPayrollProviderCompanyInformationFormFields.numberOfEmployees}
                fullWidth
                disabled={submitting}
                component={NumberField}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Primary Contact Phone"
                name={PeoPayrollProviderCompanyInformationFormFields.contactPhoneNumber}
                fullWidth
                disabled={submitting}
                component={PhoneField}
                slotProps={demoPrivacyInputSlotProps}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Field
                label="Primary Contact Email"
                name={PeoPayrollProviderCompanyInformationFormFields.contactEmail}
                fullWidth
                required
                disabled={submitting}
                component={TextField}
                slotProps={demoPrivacyInputSlotProps}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1">Headquarters address</Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <AddressFields
                prefix={PeoPayrollProviderCompanyInformationFormFields.headquarters}
                requiredStreet
                requiredCity
                requiredState
                requiredZipCode
                disabled={submitting}
                addressLabel="Street"
                inputSlotProps={demoPrivacyInputSlotProps}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <DefaultDisclaimerInputSection
                headerText={
                  isPartnership ? 'Partnership Disclaimer' : 'PEO Payroll Provider Disclaimer'
                }
                name={PeoPayrollProviderCompanyInformationFormFields.defaultDisclaimer}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box display="flex" justifyContent="center" gap={2} mt={2}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => form.reset()}
                  disabled={submitting}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting || !dirty}
                  loading={submitting}
                >
                  Save changes
                </Button>
              </Box>
            </Grid>
          </Grid>
        );
      }}
    </Form>
  );
};
